import { useState } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  type AthleteTargets,
  type CoachAthleteCard,
  type CoachCurrentMax,
  type ProgramRow,
  formatShortDate,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { Button } from "../primitives/Button";
import { MuscleCoverageCompactSection } from "../Progress/MuscleCoverageCompactSection";
import { ProgressReportSheet } from "../Progress/ProgressReportSheet";
import type { ProgressDeps } from "../Progress/types";
import type { UseChartWorkoutsOptions } from "../Progress/useChartWorkouts";
import { AthleteCardRow } from "./AthleteCardRow";
import { AthletePanelGrid } from "./AthletePanelGrid";
import { AthleteSessionsSection } from "./AthleteSessionsSection";
import { CoachTargetsPanel } from "./CoachTargetsPanel";
import type { AthleteSessionsList } from "./useAthleteSessions";
import { useAthleteDetailDerived } from "./useAthleteDetailDerived";

// <AthleteDetail> (CO-9) — the coach's per-athlete drill-down. Every panel
// RE-EXPOSES the athlete's own surfaces over the CO-11 read path — the shared
// Progress panels render VERBATIM against an athlete-scoped ProgressDeps (same
// math, same i18n, same a11y as the athlete's Progress tab), so nothing here
// can drift from what the athlete sees:
//   • identity header = the SAME AthleteCardRow the dashboard list renders
//     (rebuilt by the shared builder from the live snapshot, so header and
//     panels share one as-of; the tap-time card only shows while loading)
//   • PG-8 — the coverage census IS the headline now. The alerts panel that
//     used to sit above it is GONE from both surfaces, and its removal fixed
//     a coach-only asymmetry on the way out: its pain escalation could never
//     fire here, because ProgressDeps.painMap is host-injected and the coach
//     host hardwires `{}` (no coach-readable route carries it — it lives on
//     Profile, behind pickCoachProfileFields). The same data therefore read
//     differently to the coach and to the athlete. The census has no such
//     input, so both now read the same numbers.
//   • muscle coverage = buildMuscleCoverage + <MuscleCoverageCompactSection>
//     (ProgressView's exact idiom, prescription from the athlete's pinned block)
//   • the panel grid = <AthletePanelGrid> (the athlete's Progress panels,
//     effort coverage, rhythm, the read-only MAX list)
//   • CO-29: the athlete's Progress report, opened from the top row, the
//     base sections only (no health section is a base one), no Share
//   • #25: the athlete's sessions since the bond began, each one open set by
//     set (<AthleteSessionsSection>)
// The grid and the report are keyed on the athlete: what they hold (the PR
// history, the tag volume, the report's periods) never outlives them.
// Health panels are deliberately absent until the per-metric consent gate
// exists (backlog CO-9 note); the read path already strips them server-side.

export type AthleteDetailProps = {
  theme: UiTheme;
  /** The tap-time summary card: identity plus a stand-in header while the live
   *  read loads. Then the header is rebuilt by the same shared builder. */
  card: CoachAthleteCard;
  /** Athlete-scoped ProgressDeps: apiFetch wrapped by makeAthleteScopedApiFetch,
   *  a FRESH per-athlete resultsCache, and the athlete's own units. */
  deps: ProgressDeps;
  programs: ReadonlyArray<ProgramRow>;
  currentProgramId?: string | undefined;
  weekStartDay?: number | undefined;
  /** Current MAX map (fetchMaxesDetailed with athleteSub); null while loading. */
  maxes: Map<string, CoachCurrentMax> | null;
  /** The athlete's own weekly targets (AthleteTargets doc). ME-3: the target
   *  source, scored against the athlete's training; the pinned program stays
   *  only for the block name + the prescription 2nd reference. */
  athleteTargets: AthleteTargets | null;
  onBack: () => void;
  /** Open the coach targets editor (host swaps it in outside its ScrollView).
   *  Absent → the panel never offers «Edit». The server (PATCH /me/targets →
   *  403 no_write_grant) is the authority on whether the write lands, so «Edit»
   *  shows whenever this is wired. */
  onEditTargets?: () => void;
  /** #343: set while the editor cannot open yet (web: the athlete's unit is
   *  not read). «Edit» stays visible, disabled, with this hint. */
  editTargetsHint?: string;
  /** CO-23 — open the program/workout picker for THIS athlete (host swaps it
   *  in outside its ScrollView, same convention as `onEditTargets`).
   *  Capability-gated by omission: absent → no button renders, so a host that
   *  hasn't wired the picker (or a caller with nothing to assign from) keeps
   *  compiling and behaving exactly as before. */
  onAssignProgram?: () => void;
  /** Host defer for the volume-by-tag read: mobile InteractionManager, web
   *  requestIdleCallback (useChartWorkouts). */
  schedule?: UseChartWorkoutsOptions["schedule"];
  /** Safe-area insets for the report sheet; web passes none. */
  sheetInsets?: { top: number; bottom: number };
  /** #25: the athlete's sessions, paged (useAthleteDetail().sessions).
   *  Absent: no sessions section. */
  sessions?: AthleteSessionsList;
  /** #25: open a session (mobile pushes a route). Absent: the section shows
   *  the session itself, master-detail. */
  onOpenSession?: (sessionId: string) => void;
};

export function AthleteDetail({
  theme,
  card,
  deps,
  programs,
  currentProgramId,
  weekStartDay,
  maxes,
  athleteTargets,
  onBack,
  onEditTargets,
  editTargetsHint,
  onAssignProgram,
  schedule,
  sheetInsets,
  sessions,
  onOpenSession,
}: AthleteDetailProps) {
  const { t, i18n } = useTranslation();
  const locale = coerceLocale(i18n.language);
  const styles = makeStyles(theme);
  const [reportOpen, setReportOpen] = useState(false);

  const {
    results,
    catalog,
    error,
    coverage,
    weeklyTargets,
    liveCard,
    resolveExerciseName,
  } = useAthleteDetailDerived({
    card,
    deps,
    programs,
    currentProgramId,
    weekStartDay,
    athleteTargets,
    locale,
  });

  return (
    <View style={styles.wrap}>
      <View style={styles.backRow}>
        <Button
          theme={theme}
          variant="secondary"
          icon="chevron-left"
          label={t("coach.detail.back")}
          onPress={onBack}
        />
      </View>
      <View style={styles.actions}>
        <Button
          theme={theme}
          variant="secondary"
          icon="chart"
          label={t("mobile.progress.report.open", {
            defaultValue: "Create a report",
          })}
          onPress={() => setReportOpen(true)}
          numberOfLines={2}
          hitSlop={ROW_SLOP}
          style={styles.action}
        />
        {onAssignProgram ? (
          <Button
            theme={theme}
            variant="secondary"
            icon="users"
            label={t("coach.detail.assignProgram", {
              defaultValue: "Assign program",
            })}
            onPress={onAssignProgram}
            numberOfLines={2}
            hitSlop={ROW_SLOP}
            style={styles.action}
          />
        ) : null}
      </View>

      <AthleteCardRow theme={theme} card={liveCard} />

      <MuscleCoverageCompactSection
        theme={theme}
        coverage={coverage}
        units={deps.units}
        locale={locale}
        figure={deps.figure}
        isDark={deps.isDark}
        loading={results === null || catalog === null}
        error={error}
        viewer="coach"
      />

      {/* CO-7 / ME-3 — full-bleed, same relative slot as the athlete's own Home
          meter, above the panel grid. The targets are the athlete's own now, so
          this renders whenever there is something to score (the athlete has
          trained), independent of a pinned program; computeWeeklyTargets → null
          only when the athlete has no trained muscles at all. */}
      <CoachTargetsPanel
        theme={theme}
        data={weeklyTargets}
        units={deps.units}
        locale={locale}
        resolveExerciseName={resolveExerciseName}
        // «Edit» shows whenever the host wired the editor (i.e. the coach
        // detail). The SERVER is the authority on whether the write lands:
        // PATCH /me/targets 403s no_write_grant when the athlete hasn't granted,
        // and the editor maps that to a clear message. An earlier client-side
        // grant gate duplicated that authority and hid «Edit» deterministically
        // even with the grant live — server-authoritative is correct + robust.
        canEdit={!!onEditTargets}
        onEdit={() => onEditTargets?.()}
        editHint={editTargetsHint}
      />

      <AthletePanelGrid
        key={`grid-${card.sub}`}
        theme={theme}
        deps={deps}
        results={results}
        catalog={catalog}
        maxes={maxes}
        weekStartDay={weekStartDay}
        locale={locale}
        schedule={schedule}
      />
      {sessions ? (
        <AthleteSessionsSection
          key={`sessions-${card.sub}`}
          deps={deps}
          sessions={sessions}
          onOpenSession={onOpenSession}
        />
      ) : null}
      <ProgressReportSheet
        key={`report-${card.sub}`}
        deps={deps}
        visible={reportOpen}
        onClose={() => setReportOpen(false)}
        formatDate={formatShortDate}
        locale={locale}
        sheetInsets={sheetInsets}
        title={t("mobile.progress.report.titleOf", {
          name: card.name,
          defaultValue: "Training report: {{name}}",
        })}
      />
    </View>
  );
}

// Vertical only: horizontal slop would overlap the sibling button. 40 + 2x8
// is the 44 floor on native; on web the button's own webTapFloor holds it.
const ROW_SLOP = { top: 8, bottom: 8 } as const;

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s4, padding: s.s4 },
    // Back on its own row, leading.
    backRow: { flexDirection: "row" },
    // The report and the optional assign share the next row, half each: a
    // long DE/RU label wraps to a second line instead of clipping (the
    // overflow jl-ux-a11y-auditor found when they shared Back's row).
    actions: { flexDirection: "row", gap: s.s2 },
    action: { flex: 1 },
  });
}
